
import { useEffect } from 'react';

interface PageMetaOptions {
  title: string;
  description: string;
  path?: string;
  schemaType?: 'WebPage' | 'Article' | 'FAQPage' | 'ContactPage' | 'CollectionPage' | 'AboutPage';
  keywords?: string;
  extraSchema?: object;
}

const BASE_URL = import.meta.env.VITE_SITE_URL || 'https://eliteoffuture1.com';
const SITE_NAME = '미래인재학원';
const OG_IMAGE = 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/3f39aa3317d5241ba1cb12892109ebcf.png';
const LOGO_URL = 'https://public.readdy.ai/ai/img_res/72716b08-812d-4123-96af-8ed7c29cbb91.png';

export default function usePageMeta({
  title,
  description,
  path,
  schemaType = 'WebPage',
  keywords,
  extraSchema,
}: PageMetaOptions) {
  useEffect(() => {
    // Title
    document.title = title;

    // Last modified
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    // Description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);

    // Keywords
    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', keywords);
    }

    // OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description);

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ogImage.setAttribute('content', OG_IMAGE);

    // og:url - update or create
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    if (path) {
      ogUrl.setAttribute('content', `${BASE_URL}${path}`);
    }

    // Twitter tags
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', title);

    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', description);

    // Canonical
    if (path) {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute('href', `${BASE_URL}${path}`);
    }

    // WebPage / FAQPage / ContactPage Schema
    const schemaId = 'schema-page-meta';
    const existingSchema = document.getElementById(schemaId);
    if (existingSchema) existingSchema.remove();

    const pageUrl = path ? `${BASE_URL}${path}` : BASE_URL;

    let schemaData: object;

    if (schemaType === 'FAQPage' && extraSchema) {
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'name': title,
        'description': description,
        'url': pageUrl,
        ...extraSchema,
      };
    } else if (schemaType === 'ContactPage') {
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': title,
        'description': description,
        'url': pageUrl,
        'isPartOf': {
          '@type': 'WebSite',
          'name': SITE_NAME,
          'url': BASE_URL,
        },
        'publisher': {
          '@type': 'EducationalOrganization',
          'name': SITE_NAME,
          'url': BASE_URL,
          'logo': { '@type': 'ImageObject', 'url': LOGO_URL },
        },
      };
    } else {
      schemaData = {
        '@context': 'https://schema.org',
        '@type': schemaType,
        'name': title,
        'description': description,
        'url': pageUrl,
        'image': OG_IMAGE,
        'isPartOf': {
          '@type': 'WebSite',
          'name': SITE_NAME,
          'url': BASE_URL,
        },
        'publisher': {
          '@type': 'EducationalOrganization',
          'name': SITE_NAME,
          'url': BASE_URL,
          'logo': { '@type': 'ImageObject', 'url': LOGO_URL },
        },
        ...(extraSchema || {}),
      };
    }

    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [title, description, path, schemaType, keywords, extraSchema]);
}
